import { useMemo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTableFilters } from "@hooks/use-table-filters";
import { AxnAdminUserRpProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-rp-property";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnCountry,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnAttributeTypeFieldCustomization
} from "@idw-types/data/reference-data/axn";
import {
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    SelfServiceVerificationAttribute
} from "@idw-types/data/reference-data/generic";
import { FormattedWorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "../../common/pagination-container/pagination-container";
import { IdwTable } from "../../common/idw-table/idw-table";
import { IdwTableContainer } from "../../common/idw-table-container/idw-table-container";
import { WorkflowsTableCell } from "../workflows-table-cell/workflows-table-cell";
import styles from "./workflows-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type WorkflowsTableContainerProps = {
    workflowConfigurations: Array<FormattedWorkflowConfiguration>;
    canRestoreDueToLimits: boolean;
    isPageHeight: boolean;
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    emailTemplates: Array<FormattedEmailTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    showArchivedWorkflows: boolean;
    tableState: TableState;
    axnAdminUserRPProperties: Array<AxnAdminUserRpProperty>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

type WorkflowTableDataRow = FormattedWorkflowConfiguration & {
    companyName: string;
    relyingPartyName: string;
    displayName: string;
};

export const WorkflowsTableContainer = (props: WorkflowsTableContainerProps) => {
    const {
        workflowConfigurations,
        canRestoreDueToLimits,
        isPageHeight,
        axnAvailablePropertyCategories,
        axnCompanies,
        axnCountries,
        axnRelyingParties,
        axnIdentityProviders,
        emailTemplates,
        showArchivedWorkflows,
        tableState,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        selfServiceVerificationAttributes,
        axnAdminUserRPProperties,
        attributeClassifications,
        attributeLabels,
        attributeTypes,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps
    } = props;

    const dispatch = useDispatch();

    const workflowTableData: Array<WorkflowTableDataRow> = useMemo(() => {
        return workflowConfigurations.map(config => {
            return {
                ...config,
                companyName: config.company?.name || "",
                relyingPartyName: config.relyingParty?.name || "",
                displayName: config.status === EntityStatusTypes.INACTIVE
                    ? `(${resources.common.archived}) ${config.name}`
                    : config.name
            };

        });
    }, [workflowConfigurations]);

    const columnHelper = createColumnHelper<WorkflowTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("displayName", {
            header: resources.tables.workflows.columns.workflow.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.displayName, b.original.displayName),
            cell: (props) => {
                const filteredTemplates = emailTemplates.filter(template =>
                    (template.company === null || template.company?.id === null || template.company?.id === props.row.original.company?.id)
                    && template.status === EntityStatusTypes.ACTIVE
                );

                const defaultTemplate = emailTemplates.find(template => template.id === "1");
                if (defaultTemplate && !filteredTemplates.includes(defaultTemplate)) {
                    filteredTemplates.push(defaultTemplate);
                }

                return (
                    <WorkflowsTableCell
                        value={props.getValue()}
                        workflowConfiguration={props.row.original}
                        canRestoreDueToLimits={canRestoreDueToLimits}
                        axnCountries={axnCountries}
                        companies={axnCompanies}
                        relyingParties={axnRelyingParties}
                        axnIdentityProviders={axnIdentityProviders}
                        axnAvailablePropertyCategories={axnAvailablePropertyCategories}
                        emailTemplates={filteredTemplates}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                        selfServiceVerificationAttributes={selfServiceVerificationAttributes}
                        axnAdminUserRPProperties={axnAdminUserRPProperties}
                        attributeClassifications={attributeClassifications}
                        attributeLabels={attributeLabels}
                        attributeTypes={attributeTypes}
                        rpServiceAttributeProperties={rpServiceAttributeProperties}
                        rpServiceAttributePropertyMaps={rpServiceAttributePropertyMaps}
                    />
                );
            }
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.workflows.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("relyingPartyName", {
            header: resources.tables.workflows.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyName, b.original.relyingPartyName)
        }),
        columnHelper.accessor("changeRequestStatus", {
            header: resources.tables.workflows.columns.crStatus.header,
            filterFn: "changeRequestStatusFilter",
            cell: (props) => {
                if (props.getValue()) {
                    const original = props.row.original;

                    const renderLastModifiedTooltip = (props: any) => (
                        <Tooltip id={`workflow-${original.id}-last-modified-tooltip`} {...props}>
                            {resources.tables.workflows.tooltips.lastModified.label} {formatDateToString(original.lastModifiedDate, DISPLAY_DATE_WITH_TIME)}
                        </Tooltip>
                    );

                    return (
                        <div className={styles.changeRequestCell}>
                            <span>{props.getValue()}</span>
                            {props.row.original.lastModifiedDate && (
                                <div className={styles.lastModifiedTooltipContainer}>
                                    <OverlayTrigger placement="top" overlay={renderLastModifiedTooltip}>
                                        <span>
                                            <FontAwesomeIcon icon={faQuestionCircle} />
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("lastDeploymentDate", {
            header: resources.tables.workflows.columns.lastDeployed.header,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.lastDeploymentDate, b.original.lastDeploymentDate),
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null
        })
    ]), [workflowTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: workflowTableData,
        state: tableState,
        filterFns: filterTypes,
        autoResetPageIndex: true,
        sortDescFirst: false,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.WORKFLOWS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.WORKFLOWS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.WORKFLOWS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="workflows"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"workflows"}
                className={showArchivedWorkflows ? styles.archivedWorkflowsTable : styles.workflowsTable}
            />
            <PaginationContainer
                idPrefix="workflows"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};